/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { toast } from 'sonner';
import { Database, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { seedDummyData, purgeDummyData, seedLocalIndexedDB, clearLocalIndexedDB, countLocalIndexedDB } from '@/lib/devTools';

/** Tab "Dati": seed/purge MongoDB e IndexedDB locale. */
export const DataTab = ({ busy, withBusy, idbCount, setIdbCount, reloadStatus }) => {
  const seedBackend = () => withBusy(async () => {
    const res = await seedDummyData({ patients: 10, alerts: 5, prescriptions: 10 });
    toast.success(`Backend: +${res.seeded.patients} paz · +${res.seeded.alerts} alert · +${res.seeded.prescriptions} rx`);
    await reloadStatus();
  }, 'Seed backend fallito');

  const purgeBackend = () => {
    if (!window.confirm('Eliminare TUTTI i record marcati come "dev-generated"?')) return;
    withBusy(async () => {
      const res = await purgeDummyData();
      toast.info(`Rimossi ${res.purged.users} paz + ${res.purged.alerts} alert + ${res.purged.prescriptions} rx`);
      await reloadStatus();
    }, 'Purge fallito');
  };

  const seedIdb = () => withBusy(async () => {
    const res = await seedLocalIndexedDB();
    toast.success(`IndexedDB: ${res.patients} pazienti · ${res.alerts} alert · ${res.prescriptions} rx locali`);
    setIdbCount(res);
  }, 'IndexedDB seed fallito');

  const clearIdb = () => {
    if (!window.confirm('Svuotare IndexedDB locale?')) return;
    withBusy(async () => {
      await clearLocalIndexedDB();
      setIdbCount(await countLocalIndexedDB());
      toast.info('IndexedDB svuotato');
    }, 'Svuotamento fallito');
  };

  return (
    <>
      <section className="rounded-lg border border-border p-3 space-y-2">
        <div className="flex items-center gap-2"><Database className="h-3.5 w-3.5" /><div className="text-xs font-bold">Backend seed (MongoDB)</div></div>
        <div className="text-[10px] text-muted-foreground">Inietta 10 pazienti + 5 alert urgenti + 10 prescrizioni marcati <code>__dev_generated</code>.</div>
        <div className="flex gap-2">
          <Button size="sm" onClick={seedBackend} disabled={busy} data-testid="seed-backend-btn" className="flex-1"><Database className="h-3 w-3 mr-1" /> Popola DB</Button>
          <Button size="sm" variant="outline" onClick={purgeBackend} disabled={busy} data-testid="purge-backend-btn"><Trash2 className="h-3 w-3" /></Button>
        </div>
      </section>
      <section className="rounded-lg border border-border p-3 space-y-2">
        <div className="flex items-center gap-2"><Database className="h-3.5 w-3.5" /><div className="text-xs font-bold">IndexedDB locale (offline)</div></div>
        <div className="text-[10px] text-muted-foreground">10 pazienti + 5 alert + 12 ricette in <code>sanitanexus_dev_seed</code>. Solo browser.</div>
        {idbCount && <div className="text-[10px] font-mono bg-muted/40 rounded px-2 py-1">{idbCount.patients} paz · {idbCount.alerts} alert · {idbCount.prescriptions} rx</div>}
        <div className="flex gap-2">
          <Button size="sm" onClick={seedIdb} disabled={busy} className="flex-1" data-testid="seed-idb-btn">Popola IndexedDB</Button>
          <Button size="sm" variant="outline" onClick={clearIdb} disabled={busy} data-testid="clear-idb-btn"><Trash2 className="h-3 w-3" /></Button>
        </div>
      </section>
    </>
  );
};
